import { useMemo, useState } from 'react'
import type { ParamType } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { TypedValueInput } from '@/components/compliance/TypedValueInput'

/**
 * A single field in a `presentForm` request. Generic field-descriptor shared by
 * any tool that needs structured input (runbook inputs, API-check params, …).
 */
export interface ChatFormField {
  name: string
  label?: string | null
  type?: ParamType | null
  description?: string | null
  required?: boolean | null
  options?: string[] | null
  defaultValue?: unknown
}

export interface ChatFormSpec {
  title: string
  intro?: string | null
  submitLabel?: string | null
  fields: ChatFormField[]
}

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  )
}

function withoutWrappingQuotes(value: string): string {
  return value.trim().replace(/^["'“”‘’]|["'“”‘’]$/g, '')
}

/**
 * The interactive input form rendered in chat for a `presentForm` deferred tool.
 * On submit it hands the collected values to the caller, which routes them back
 * to the agent via `addToolOutput` (so the agent re-calls the original tool with
 * the values). Reuses `TypedValueInput` so chat fields match the run-dialog ones.
 */
export function ChatInputFormCard({
  spec,
  onSubmit,
  onCancel,
  disabled,
}: {
  spec: ChatFormSpec
  onSubmit: (values: Record<string, unknown>) => void
  onCancel: () => void
  disabled?: boolean
}) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {}
    for (const field of spec.fields) {
      if (field.defaultValue != null) initial[field.name] = field.defaultValue
    }
    return initial
  })

  const setField = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }))

  const missingRequired = useMemo(
    () =>
      spec.fields.filter(
        (field) => field.required !== false && isEmpty(values[field.name])
      ),
    [spec.fields, values]
  )

  return (
    <div className='animate-in fade-in slide-in-from-bottom-2 border-border overflow-hidden rounded-md border bg-card shadow-[0_2px_16px_-8px_rgba(16,24,40,0.12)] duration-200 ease-out'>
      <div className='border-b border-border-faint px-4 py-2.5'>
        <div className='text-text-secondary text-[12px] font-semibold'>
          {withoutWrappingQuotes(spec.title)}
        </div>
        {spec.intro && (
          <p className='text-text-subtle mt-0.5 text-[11px] leading-relaxed'>
            {withoutWrappingQuotes(spec.intro)}
          </p>
        )}
      </div>

      <ScrollArea type='always' viewClassName='max-h-[50vh] [&>div]:!block'>
        <div className='space-y-3 px-4 py-3'>
          {spec.fields.map((field) => {
            const id = `chat-form-${field.name}`
            const required = field.required !== false
            const type = (field.type ?? 'string') as ParamType
            return (
              <div key={field.name} className='space-y-1.5'>
                <Label
                  htmlFor={id}
                  className='text-text-secondary flex items-center gap-1 text-[12px] font-medium'
                >
                  {withoutWrappingQuotes(field.label || field.name)}
                  {required && <span className='text-cobalt-strong'>*</span>}
                  <span className='text-text-placeholder font-mono text-[10px]'>
                    {type}
                  </span>
                </Label>
                {field.description && (
                  <p className='text-text-subtle text-[11px] leading-relaxed'>
                    {withoutWrappingQuotes(field.description)}
                  </p>
                )}
                <TypedValueInput
                  type={type}
                  id={id}
                  value={values[field.name]}
                  enumOptions={field.options ?? undefined}
                  disabled={disabled}
                  onChange={(next) => setField(field.name, next)}
                  className='bg-card'
                  placeholder={required ? 'Required' : 'Optional'}
                />
              </div>
            )
          })}
        </div>
      </ScrollArea>

      <div className='flex items-center justify-end gap-2 border-t border-border-faint px-4 py-2.5'>
        <Button
          variant='ghost'
          size='sm'
          disabled={disabled}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size='sm'
          className='bg-primary text-primary-foreground hover:bg-primary/90'
          disabled={disabled || missingRequired.length > 0}
          onClick={() => onSubmit(values)}
        >
          {spec.submitLabel
            ? withoutWrappingQuotes(spec.submitLabel)
            : 'Submit'}
        </Button>
      </div>
    </div>
  )
}
